
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Typography,
  Box,
  Rating,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import api from "../../../services/api";

// ---------------- STYLES ----------------
const PopupWrapper = styled(Box)({
  width: "480px",
  padding: "28px",
  borderRadius: "16px",
  border: "1px solid #ddd",
  boxSizing: "border-box",
});

const StyledTextField = styled(TextField)({
  width: "100%",
  marginTop: "10px",
});

const StyledButton = styled(Button)({
  marginTop: "20px",
  background: "linear-gradient(90deg, #cc7103 0%, #d2c75f 50%,  #0069d9 100%)",
  borderRadius: 8,
  color: "white",
  height: 48,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "16px",
  "&:hover": {
    background: "linear-gradient(90deg, #d57a0b 0%, #c5ba59 50%, #007BFF 100%)",
  },
});

export default function RatingDialog({onSubmitSuccess}) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [question, setQuestion] = useState(""); // ⭐ Dynamic question
  const [title, setTitle] = useState("");
  const [bookslotId, setBookslotId] = useState(null);
  const [loading, setLoading] = useState(false);

  const isSubmitDisabled = rating === 0 || feedback.trim().length === 0;

  // -------- Fetch popup status ----------
  useEffect(() => {
    const fetchPopupStatus = async () => {
      try {
        const token = localStorage.getItem("accessToken");

        const res = await api.get("/event/fdp-spoc-rating-status/", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data.show_popup === 1) {
          setTitle(res.data.title);
          setBookslotId(res.data.bookslot_id);
          setOpen(true);
        }
      } catch (err) {
        console.error("Popup fetch failed:", err);
      }
    };

    fetchPopupStatus();
  }, []);

  // -------- Handlers ----------
  const handleFeedbackChange = (e) => {
    if (e.target.value.length <= 400) setFeedback(e.target.value);
  };

  const handleRatingChange = (e, value) => {
    setRating(value);

    if (!value) return;

    // ⭐ Dynamic question based on rating
    if (value <= 3) {
      setQuestion(
        "We regret to know that your experience with us wasn't great. How could we serve you better next time?"
      );
    } else {
      setQuestion(
        "We're delighted to hear that you had a great learning with us! What made your experience special?"
      );
    }
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("accessToken");

      const res = await api.post(
        "/event/fdp-rating-submit/",
        {
          bookslot_id: bookslotId,
          rating: rating,
          feedback: feedback,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert(res.data.detail || "Thank you!");

      console.log("Calling onSubmitSuccess from RatingDialog");
      if (onSubmitSuccess) onSubmitSuccess();

      setOpen(false);
    } catch (err) {
      console.error(err);
      alert("Failed to submit feedback!");
    }

    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      PaperProps={{
        sx: { borderRadius: "18px" },
      }}
    >
      <DialogContent>
        <PopupWrapper>
          <DialogTitle sx={{ textAlign: "center", fontSize: "22px", p: 0 }}>
            <Typography fontWeight={600} color="#FF8E53">
              Rate your experience
            </Typography>
          </DialogTitle>

          <Typography
            variant="body1"
            color="gray"
            textAlign="center"
            mt={1}
            mb={3}
          >
            {title}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Box textAlign="center" mb={2}>
              <Rating
                name="rating"
                value={rating}
                onChange={handleRatingChange}
                size="large"
              />
            </Box>

            {/* ⭐ Q → A UI (Question outside the textbox) */}
            <Box mt={2}>
              {rating > 0 && (
                <Typography
                  fontSize="15px"
                  fontWeight={500}
                  mb={1}
                  textAlign="left"
                  sx={{
                    lineHeight: 1.4,
                    color: (theme) => theme.palette.text.primary,  // ⭐ Auto-light/dark text
                  }}
                >
                  {question}
                </Typography>
              )}

              {rating > 0 && (<StyledTextField
                multiline
                rows={5}
                placeholder="Type your answer here..."
                value={feedback}
                onChange={handleFeedbackChange}
                disabled={rating === 0}
                inputProps={{ maxLength: 400 }}
              />
              )}
            </Box>

            <StyledButton
              fullWidth
              type="submit"
              variant="contained"
              disabled={isSubmitDisabled || loading}
            >
              {loading ? "Submitting..." : "Submit Feedback"}
            </StyledButton>
          </form>
        </PopupWrapper>
      </DialogContent>
    </Dialog>
  );
}
